import React from 'react';

import { useRouter } from 'next/router';
import { AwardModalTypes, ModalTypes } from '../../context/modalContext';
import { useModal, useRegister } from '../../hooks';
import styles from './account.module.sass';
import { User } from '../../types/user';
import { BusinessRegisterStatuses } from '../../helpers/constants';
import { PrismicAwardSettings, PrismicAccount } from '../../types/prismic';
import { RegisterSteps } from '../../context/registerContext';
import AccountStatus from './AccountStatus';
import SavedBoards from './SavedBoards';
import BusinessProfile from './BusinessProfile';
import AwardProcess from './AwardProcess';
import GenericAwardProcess from './GenericAwardProcess';
import SephoraAwardProcess from './SephoraAwardProcess';

type AccountDoc = PrismicAwardSettings & PrismicAccount;

type Props = {
  content: AccountDoc;
  award2023Sephora: AccountDoc;
  award2025: AccountDoc;
  awardVictoriasSecret: AccountDoc;
  user: User;
};

export enum BusinessStatus {
  NOT_CREATED = 'not-created',
  CREATED = 'created',
  UPDATED = 'update',
  NEEDS_UPDATE = 'needs-update',
}

export enum GenericAwardStatus {
  PROFILE_UPDATE_REQUIRED = 'profile-update-required',
  AVAILABLE = 'available',
  SUBMITTED = 'submitted',
}

export enum AwardStatus {
  PROFILE_UPDATE_REQUIRED = 'profile-update-required',
  AVAILABLE = 'available',
  SUBMITTED = 'submitted',
}

export enum SephoraAwardStatus {
  PROFILE_UPDATE_REQUIRED = 'profile-update-required',
  AVAILABLE = 'available',
  SUBMITTED = 'submitted',
}

const calculateAwardStatus = (
  businessCreated: boolean,
  businessUpdated: boolean,
  businessNeedsUpdate: boolean,
  awardSubmitted: boolean
) => {
  if (awardSubmitted) return AwardStatus.SUBMITTED;
  if (businessCreated && businessUpdated && !businessNeedsUpdate)
    return AwardStatus.AVAILABLE;
  return AwardStatus.PROFILE_UPDATE_REQUIRED;
};

const calculateSephoraAwardStatus = (
  businessCreated: boolean,
  businessUpdated: boolean,
  businessNeedsUpdate: boolean,
  sephoraAwardSubmitted: boolean
) => {
  if (sephoraAwardSubmitted) return SephoraAwardStatus.SUBMITTED;
  if (businessCreated && businessUpdated && !businessNeedsUpdate)
    return SephoraAwardStatus.AVAILABLE;
  return SephoraAwardStatus.PROFILE_UPDATE_REQUIRED;
};

const calculateGenericAwardStatus = (
  businessCreated: boolean,
  businessUpdated: boolean,
  businessNeedsUpdate: boolean,
  awardSubmitted: boolean
) => {
  if (awardSubmitted) return GenericAwardStatus.SUBMITTED;
  if (businessCreated && businessUpdated && !businessNeedsUpdate)
    return GenericAwardStatus.AVAILABLE;
  return GenericAwardStatus.PROFILE_UPDATE_REQUIRED;
};

const calculateBusinessStatus = (
  businessCreated: boolean,
  businessUpdated: boolean,
  businessNeedsUpdate: boolean
) => {
  let businessProfileStatus = BusinessStatus.NOT_CREATED;
  if (businessCreated) {
    if (businessNeedsUpdate) {
      businessProfileStatus = BusinessStatus.NEEDS_UPDATE;
    } else {
      businessProfileStatus = businessUpdated
        ? BusinessStatus.UPDATED
        : BusinessStatus.CREATED;
    }
  }
  return businessProfileStatus;
};

const Account = ({
  content,
  user,
  award2025,
  award2023Sephora,
  awardVictoriasSecret,
}: Props) => {
  const { setModalType, setAwardModalType } = useModal();
  const { setRegisterStep } = useRegister();
  const router = useRouter();

  if (!content) return null;
  if (!user) return null;

  const userId = user.id;

  // state values tied to backend data
  const hasBusiness = user.business ? true : false;
  const verifiedEmail = user && user.data.enabled ? true : false; // we may want to add email_verified column later
  const businessCreated = hasBusiness;
  const businessUpdated =
    hasBusiness &&
    user.business.data.registerStatus === BusinessRegisterStatuses.COMPLETE;
  const businessNeedsUpdate =
    hasBusiness &&
    user.business.data.registerStatus ===
      BusinessRegisterStatuses.COMPLETED_NEEDS_UPDATE;
  const savedBoardsEnabled = verifiedEmail;

  const awardProcessClosed = !award2025.data.award_process_active;
  const awardSephoraProcessClosed = !award2023Sephora.data.award_process_active;
  const awardVsProcessClosed = !awardVictoriasSecret.data.award_process_active;

  // let awardProcessClosed = false;  // TODO: this is only for editing
  // let awardSephoraProcessClosed = false;

  const awardSubmitted =
    hasBusiness && user.business.data.award2025TypeformId ? true : false;
  const awardStatus = calculateAwardStatus(
    businessCreated,
    businessUpdated,
    businessNeedsUpdate,
    awardSubmitted
  );

  // Sephora awardstatus
  const sephoraAwardSubmitted =
    hasBusiness && user.business.data.award2025SephoraTypeformId ? true : false;
  const sephoraAwardStatus = calculateSephoraAwardStatus(
    businessCreated,
    businessUpdated,
    businessNeedsUpdate,
    sephoraAwardSubmitted
  );

  // VS awardstatus
  const victoriasSecretAwardSubmitted =
    hasBusiness && user.business.data.award2025VsTypeformId ? true : false;
  const victoriasSecretAwardStatus = calculateGenericAwardStatus(
    businessCreated,
    businessUpdated,
    businessNeedsUpdate,
    victoriasSecretAwardSubmitted
  );

  const businessProfileStatus = calculateBusinessStatus(
    businessCreated,
    businessUpdated,
    businessNeedsUpdate
  );

  const awardApplicationEnabled =
    !awardProcessClosed && awardStatus === AwardStatus.AVAILABLE;

  const awardSephoraApplicationEnabled =
    !awardSephoraProcessClosed &&
    sephoraAwardStatus === SephoraAwardStatus.AVAILABLE;

  const handleBusinessClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (businessUpdated) {
      // have a full business, redirect to the public business profile
      router.push(`/business/` + user.business.data.slug);
    } else {
      setModalType(ModalTypes.REGISTER_BUSINESS);
      setRegisterStep(RegisterSteps.BUSINESS);
    }
  };
  const handleAwardClick = () => {
    if (awardStatus === AwardStatus.AVAILABLE) {
      setModalType(ModalTypes.REGISTER_BUSINESS_FOR_AWARD);
    }
  };

  const handleSephoraAwardClick = () => {
    if (sephoraAwardStatus === SephoraAwardStatus.AVAILABLE) {
      setModalType(ModalTypes.REGISTER_BUSINESS_FOR_SEPHORA_AWARD);
    }
  };
  const handleGenericAwardClick = (
    awardModalType: AwardModalTypes,
    awardStatus: GenericAwardStatus
  ) => {
    return () => {
      if (awardStatus === GenericAwardStatus.AVAILABLE) {
        setModalType(ModalTypes.REGISTER_BUSINESS_FOR_GENERIC_AWARD);
        setAwardModalType(awardModalType);
      }
    };
  };

  const { account_header, account_welcome } = content.data;

  return (
    <section className="section-first inner-width-padding inner-width">
      <article className="column center">
        <h3>{account_header}</h3>
        <p>
          {account_welcome}
          {/* {businessProfileStatus === BusinessStatus.NOT_CREATED && (
            <>
              {account_create_brand_conversational_text}{' '}
              <a href="" onClick={handleBusinessClick}>{account_create_brand_header}</a>
            </>
          )}
          {businessProfileStatus === BusinessStatus.CREATED && (
            <>
              {account_update_brand_conversational_text}{' '}
              <a href="" onClick={handleBusinessClick}>
                {account_update_brand_header}
              </a>
            </>
          )} */}
        </p>

        <div className={styles.account}>
          <AccountStatus
            userId={userId}
            email={user.data.email}
            isEmailVerified={verifiedEmail}
            content={content}
          />
          <SavedBoards
            userId={userId}
            savedBoardsEnabled={savedBoardsEnabled}
            content={content}
          />
          <BusinessProfile
            handleBusinessClick={handleBusinessClick}
            businessProfileStatus={businessProfileStatus}
            content={content}
          />
          {/* normal */}
          <AwardProcess
            awardApplicationEnabled={awardApplicationEnabled}
            awardProcessClosed={awardProcessClosed}
            awardStatus={awardStatus}
            handleAwardClick={handleAwardClick}
            content={award2025}
          />
          {/* sephora */}
          <SephoraAwardProcess
            awardSephoraApplicationEnabled={awardSephoraApplicationEnabled}
            awardSephoraProcessClosed={awardSephoraProcessClosed}
            sephoraAwardStatus={sephoraAwardStatus} // TODO make new awardProcess in total
            handleSephoraAwardClick={handleSephoraAwardClick}
            content={award2023Sephora}
          />
          {/* Configure our generic award process btn for the specific VS award */}
          <GenericAwardProcess
            processClosed={awardVsProcessClosed}
            awardStatus={victoriasSecretAwardStatus}
            handleAwardClick={handleGenericAwardClick(
              AwardModalTypes.VICTORIAS_SECRET_PROCESS,
              victoriasSecretAwardStatus
            )}
            content={awardVictoriasSecret}
          />
        </div>
      </article>
    </section>
  );
};

export default Account;
